import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

import { cx } from './cx';
import { ToastContext, type Toast, type ToastApi, type ToastOptions } from './toastContext';

/**
 * Transient feedback for a mutation.
 *
 * Bottom of the screen on a phone, top right on a desk — see ui.css. The stack
 * is capped so a flaky connection retrying six times does not bury the screen
 * in its own error messages.
 */

const MAX_VISIBLE = 4;
const DEFAULT_DURATION_MS = 4000;

/** An absent title is an absent key, not a key holding undefined. */
function titleOf(title: string | undefined): { title?: string } {
  return title === undefined ? {} : { title };
}

interface ToastProviderProps {
  children: ReactNode;
}

export function ToastProvider({ children }: ToastProviderProps) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const timers = useRef(new Map<string, number>());

  const dismiss = useCallback((id: string) => {
    const timer = timers.current.get(id);
    if (timer !== undefined) {
      window.clearTimeout(timer);
      timers.current.delete(id);
    }
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const show = useCallback(
    (options: ToastOptions): string => {
      const id = crypto.randomUUID();
      const tone = options.tone ?? 'info';
      // An error holds until dismissed; anything else clears itself.
      const duration = options.durationMs ?? (tone === 'error' ? 0 : DEFAULT_DURATION_MS);

      const toast: Toast = { ...options, id, tone };
      setToasts((current) => [...current, toast].slice(-MAX_VISIBLE));

      if (duration > 0) {
        timers.current.set(
          id,
          window.setTimeout(() => {
            dismiss(id);
          }, duration),
        );
      }
      return id;
    },
    [dismiss],
  );

  // Every pending timer is a callback into a component that may be gone.
  useEffect(() => {
    const pending = timers.current;
    return () => {
      for (const timer of pending.values()) window.clearTimeout(timer);
      pending.clear();
    };
  }, []);

  const api = useMemo<ToastApi>(
    () => ({
      show,
      success: (message, title) => show({ message, tone: 'success', ...titleOf(title) }),
      error: (message, title) => show({ message, tone: 'error', ...titleOf(title) }),
      info: (message, title) => show({ message, tone: 'info', ...titleOf(title) }),
      dismiss,
    }),
    [show, dismiss],
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      {/*
        One live region for the whole stack. 'assertive' for the region means an
        error is read as soon as it lands, which is the point of the error path;
        the individual toasts are plain children so nothing is announced twice.
      */}
      <div className="dc-toasts" role="log" aria-live="assertive" aria-relevant="additions">
        {toasts.map((toast) => (
          <div key={toast.id} className={cx('dc-toast', `dc-toast--${toast.tone}`)}>
            <div className="dc-toast__body">
              {toast.title !== undefined && <p className="dc-toast__title">{toast.title}</p>}
              <p className="dc-toast__message">{toast.message}</p>
            </div>
            <button
              type="button"
              className="dc-toast__close"
              aria-label="Dismiss"
              onClick={() => {
                dismiss(toast.id);
              }}
            >
              &times;
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}
